import { Observable } from 'rxjs';

import { OrgContext } from '../models/organization.model';
import { OrgWheel, OrgWheelAnswers } from '../models/org-wheel.model';
import {
  OrgEngagement,
  OrgEngagementOverview,
  OrgProgressOp,
  OrgProgressResult,
  OrgPulse,
} from '../models/org-engagement.model';
import { OrgHealthCatalog, OrgHealthSample, OrgHealthSyncResult } from '../models/org-health.model';
import {
  OrgBookingResult,
  OrgCounselingOptions,
  OrgCounselingSession,
  OrgGuardianDecisionResult,
  OrgGuardianRequest,
} from '../models/org-counseling.model';
import { OrgChild, OrgChildDigest, OrgConsentDecision, OrgConsentRequest } from '../models/org-guardian.model';

/**
 * OrganizationsRepository — the DDD port for every member-facing B2B endpoint in
 * `B2B-ORGANIZATIONS-FE-INTEGRATION.md`.
 *
 * Implementations (infrastructure/repositories/):
 *   · OrganizationsHttpRepository — the real `/api/v3/org/*` + `/api/v3/health/*` binding
 *   · OrganizationsMockRepository — designed local payloads, for offline work
 *
 * The application layer depends only on this abstraction.
 *
 * NOTHING here swallows an error. Every refusal in this document carries meaning
 * — a `403` on a challenge means "not yours", a `409 slot_full` is a normal race
 * the UI retries past — so failures propagate to the facades, which map them
 * through the shared `OrgErrorMapper` and decide what the member sees. The one
 * exception is documented per-method below (`getContext`), where a failure is
 * genuinely indistinguishable from "not a member" for rendering purposes.
 */
export abstract class OrganizationsRepository {
  // ── §2 context ──────────────────────────────────────────────────────────
  /**
   * `GET /api/v3/org/context`. Never 404s and never 403s for a signed-in user —
   * a non-member is a 200 with `member: null`, which maps to `null` here.
   */
  abstract getContext(): Observable<OrgContext>;

  // ── §3 the wheel ────────────────────────────────────────────────────────
  /**
   * `GET /api/v3/org/wheel`. Works for a NON-MEMBER too (the shared catalogue
   * with no org customisation), so it may be called without checking context.
   */
  abstract getWheel(): Observable<OrgWheel>;

  /**
   * `POST /api/v3/org/wheel/check-in` — returns THE ENTIRE WHEEL, identical in
   * shape to {@link getWheel}. Re-render from the response; do not follow with a
   * GET. Unknown keys are dropped silently with a 200, so callers must send only
   * keys from the wheel they just rendered.
   */
  abstract submitCheckin(answers: OrgWheelAnswers): Observable<OrgWheel>;

  // ── §4 engagement ───────────────────────────────────────────────────────
  /** `GET /api/v3/org/engagement` — challenges, streak, badges and pulse in one read. */
  abstract getEngagement(): Observable<OrgEngagement>;

  /**
   * `POST /api/v3/org/engagement/challenges/{id}/join`. No body. Joining twice is
   * NOT an error — it returns the existing participation. Answers with the
   * PARTIAL overview shape, never the full engagement payload.
   */
  abstract joinChallenge(challengeId: number): Observable<OrgEngagementOverview>;

  /**
   * `POST /api/v3/org/engagement/challenges/{id}/progress`. Self-reported
   * challenges only. `date` defaults to today server-side; `op` defaults to `set`.
   */
  abstract reportProgress(
    challengeId: number,
    value: number,
    op: OrgProgressOp,
    date?: string,
  ): Observable<OrgProgressResult>;

  /**
   * `POST /api/v3/org/engagement/pulse`. Returns the PULSE BLOCK ONLY. Re-answering
   * before the week ends overwrites. 404 when there is no active question.
   */
  abstract answerPulse(value: number): Observable<OrgPulse>;

  // ── §5 health samples ───────────────────────────────────────────────────
  /** `GET /api/v3/health/metrics` — call on first sync and cache. */
  abstract getHealthCatalog(): Observable<OrgHealthCatalog>;

  /**
   * `POST /api/v3/health/samples`. PARTIAL ACCEPTANCE is the design: a 200 with
   * `accepted: 0` is entirely possible, so the result must be read, never inferred.
   */
  abstract syncHealthSamples(samples: readonly OrgHealthSample[]): Observable<OrgHealthSyncResult>;

  // ── §7 school counseling ────────────────────────────────────────────────
  /** `GET /api/v3/org/counseling/options` — two different shapes; branch on `available`/`reason`. */
  abstract getCounselingOptions(): Observable<OrgCounselingOptions>;

  /** `POST /api/v3/org/counseling/bookings` — `{ slot_id, date }`, both required. */
  abstract bookCounseling(slotId: number, date: string): Observable<OrgBookingResult>;

  /** `GET /api/v3/org/counseling/bookings` — the member's own, newest first, capped at 20. */
  abstract getCounselingSessions(): Observable<readonly OrgCounselingSession[]>;

  /**
   * `POST /api/v3/org/counseling/bookings/{id}/cancel`. Body optional. Only an
   * OPEN session (pending or confirmed) can be cancelled; anything else is a 404
   * carrying `data.reason`.
   */
  abstract cancelCounseling(sessionId: number, reason?: string | null): Observable<readonly OrgCounselingSession[]>;

  // ── §8 guardian ─────────────────────────────────────────────────────────
  /** `GET /api/v3/org/children` — `{list: []}` with a 200 for a non-guardian. Never 403. */
  abstract getChildren(): Observable<readonly OrgChild[]>;

  /** `GET /api/v3/org/children/{member_id}/digest` — the path id is an ORG MEMBER id. */
  abstract getChildDigest(memberId: number): Observable<OrgChildDigest>;

  /** `GET /api/v3/org/consents` — referrals waiting on this guardian's decision. */
  abstract getConsents(): Observable<readonly OrgConsentRequest[]>;

  /** `POST /api/v3/org/consents/{referral_id}` — `{ granted, note? }`. */
  abstract decideConsent(referralId: number, granted: boolean, note?: string | null): Observable<OrgConsentDecision>;

  /** `GET /api/v3/org/counseling/guardian-requests` — §7.3 sessions plus the child's name. */
  abstract getGuardianRequests(): Observable<readonly OrgGuardianRequest[]>;

  /** `POST /api/v3/org/counseling/guardian-requests/{session_id}` — `{ approve }`. */
  abstract decideGuardianRequest(sessionId: number, approve: boolean): Observable<OrgGuardianDecisionResult>;
}
